import { useCallback, useState, useTransition } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import type { PostStatus } from '@/types/post';
import type { PostSearchParams, Sort } from '@/types/search';

export function useSearchFilters() {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();

  const [filters, setFilters] = useState<PostSearchParams>(() => {
    return {
      query: searchParams.get('query') || '',
      location: searchParams.get('location') || undefined,
      status: (searchParams.get('status') as PostStatus) || undefined,
      skills: searchParams.get('skills')?.split(',').filter(Boolean) || [],
      sort: (searchParams.get('sort') as Sort) || 'newest',
      page: Number(searchParams.get('page')) || 1,
    };
  });

  const applyFilters = useCallback(() => {
    const hasFiltersToApply =
      filters.location !== undefined ||
      (filters.skills && filters.skills.length > 0);

    const hasStatusChange =
      searchParams.get('status') !== (filters.status || null);

    if (!hasFiltersToApply && !hasStatusChange) {
      return;
    }

    const params = new URLSearchParams(searchParams.toString());

    // Only include query if it exists in current URL
    const currentQuery = searchParams.get('query');
    if (currentQuery) {
      params.set('query', currentQuery);
    }

    if (filters.location !== undefined)
      params.set('location', filters.location);
    if (filters.sort && filters.sort !== 'newest')
      params.set('sort', filters.sort);
    if (filters.status !== undefined) {
      params.set('status', filters.status);
    } else {
      params.delete('status');
    }

    if (filters.skills && filters.skills.length > 0) {
      params.set('skills', filters.skills.join(','));
    } else {
      params.delete('skills');
    }

    startTransition(() => {
      if (pathname === '/search') {
        router.push(`${pathname}?${params.toString()}`);
      } else {
        router.push(`/search?${params.toString()}`);
      }
    });
  }, [filters, pathname, router, searchParams]);

  const clearFilters = useCallback(() => {
    const hasFiltersToClear =
      filters.location !== undefined ||
      filters.status !== undefined ||
      (filters.skills && filters.skills.length > 0);

    if (!hasFiltersToClear) {
      return;
    }

    const newParams = new URLSearchParams(searchParams.toString());
    // Preserve query from URL if it exists
    const currentQuery = searchParams.get('query');
    if (currentQuery) {
      newParams.set('query', currentQuery);
    }

    if (filters.sort && filters.sort !== 'newest') {
      newParams.set('sort', filters.sort);
    } else {
      newParams.delete('sort');
    }

    newParams.delete('location');
    newParams.delete('status');
    newParams.delete('skills');

    startTransition(() => {
      setFilters({
        query: currentQuery || '', // Use current query from URL
        sort: filters.sort,
        location: undefined,
        status: undefined,
        skills: [],
        page: 1,
      });
      if (pathname === '/search') {
        router.push(`${pathname}?${newParams.toString()}`);
      }
    });
  }, [filters, pathname, router, searchParams]);

  const updateFilter = useCallback(
    <K extends keyof PostSearchParams>(key: K, value: PostSearchParams[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    [],
  );

  const removeFilter = useCallback(
    (filterValue: string) => {
      if (filters.skills?.includes(filterValue)) {
        const updatedSkills = filters.skills.filter(
          (skill) => skill !== filterValue,
        );
        updateFilter(
          'skills',
          updatedSkills.length ? updatedSkills : undefined,
        );
      } else if (
        filters.location !== undefined &&
        filters.location === filterValue
      ) {
        updateFilter('location', undefined);
      } else if (filters.status === filterValue) {
        updateFilter('status', undefined);
      }

      const params = new URLSearchParams(searchParams.toString());

      if (filters.skills?.includes(filterValue)) {
        const updatedSkills = filters.skills.filter(
          (skill) => skill !== filterValue,
        );
        if (updatedSkills.length > 0) {
          params.set('skills', updatedSkills.join(','));
        } else {
          params.delete('skills');
        }
      } else if (params.get('location') === filterValue) {
        params.delete('location');
      } else if (params.get('status') === filterValue) {
        params.delete('status');
      }

      startTransition(() => {
        if (pathname === '/search') {
          router.push(`${pathname}?${params.toString()}`);
        } else {
          router.push(`/search?${params.toString()}`);
        }
      });
    },
    [filters, searchParams, pathname, router, updateFilter],
  );

  const updateSort = useCallback(
    (value: Sort) => {
      setFilters((prev) => ({ ...prev, sort: value }));

      const params = new URLSearchParams(searchParams.toString());

      if (value === 'newest') {
        params.delete('sort');
      } else {
        params.set('sort', value);
      }

      startTransition(() => {
        if (pathname === '/search') {
          router.push(`${pathname}?${params.toString()}`);
        } else {
          router.push(`/search?${params.toString()}`);
        }
      });
    },
    [searchParams, pathname, router],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set('page', page.toString());

      startTransition(() => {
        setFilters((prev) => ({ ...prev, page }));
        if (pathname === '/search') {
          router.push(`${pathname}?${params.toString()}`);
        } else {
          router.push(`/search?${params.toString()}`);
        }
      });
    },
    [pathname, router, searchParams],
  );

  return {
    filters,
    updateFilter,
    applyFilters,
    clearFilters,
    removeFilter,
    updateSort,
    handlePageChange,
    isPending,
  };
}
